import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  Alert,
  Fade,
  LinearProgress,
  Chip,
  Paper,
  Grid,
  Avatar,
  useTheme,
  useMediaQuery,
  Zoom,
  Grow,
  Stack,
  Divider
} from '@mui/material';
import {
  LocalFlorist as EcoIcon,
  Assessment as AssessmentIcon,
  TrendingUp as TrendingUpIcon,
  Timeline as TimelineIcon,
  Business as FactoryIcon,
  Speed as SpeedIcon
} from '@mui/icons-material';
import CarbonFootprintForm from './CarbonFootprintForm';
import CarbonFootprintReport from './CarbonFootprintReport';
import MDButton from '../../components/MDButton';

/**
 * Ana Karbon Ayak İzi Hesaplama Sayfası
 * Form workflow'unu ve state management'ı yönetir
 */
const CarbonCalculator = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [calculationData, setCalculationData] = useState(null);
  const [savedCalculations, setSavedCalculations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const steps = [
    {
      label: 'Sektör Seçimi ve Veri Girişi',
      icon: <FactoryIcon />,
      description: 'İşletmenizin sektörünü seçin ve veri girişi yapın'
    },
    {
      label: 'Hesaplama Sonuçları',
      icon: <AssessmentIcon />,
      description: 'Karbon ayak izi hesaplama sonuçlarını görüntüleyin'
    },
    {
      label: 'Rapor ve Dışa Aktarım',
      icon: <TimelineIcon />,
      description: 'Detaylı raporu inceleyin ve dışa aktarın'
    }
  ];

  // Kaydedilmiş hesaplamaları localStorage'dan yükle
  useEffect(() => {
    try {
      const saved = localStorage.getItem('carbonCalculations');
      if (saved) {
        setSavedCalculations(JSON.parse(saved));
      }
    } catch (error) {
      console.error('Error loading saved calculations:', error);
    }
  }, []);

  // Hesaplama tamamlandığında
  const handleCalculationComplete = (data) => {
    setIsLoading(true);
    
    // Kısa bir delay ekle (UX için)
    setTimeout(() => {
      setCalculationData(data);
      setActiveStep(1);
      
      // Hesaplamayı kaydet
      saveCalculation(data);
      setIsLoading(false);
    }, 1000);
  };

  // Hesaplamayı localStorage'a kaydet
  const saveCalculation = (data) => {
    try {
      const calculationRecord = {
        id: Date.now(),
        timestamp: new Date().toISOString(),
        sector: data.formData.sector,
        totalEmissions: data.results.total_emissions.ton_co2e,
        sectorIntensity: data.results.sector_intensity,
        data: data
      };

      const updated = [calculationRecord, ...savedCalculations.slice(0, 9)]; // Son 10 hesaplama
      setSavedCalculations(updated);
      localStorage.setItem('carbonCalculations', JSON.stringify(updated));
    } catch (error) {
      console.error('Error saving calculation:', error);
    }
  };

  // Yeni hesaplama başlat
  const handleReset = () => {
    setCalculationData(null);
    setActiveStep(0);
    setIsLoading(false);
  };

  // Kaydedilmiş hesaplamayı yükle
  const loadSavedCalculation = (calculation) => {
    setCalculationData(calculation.data);
    setActiveStep(1);
  };

  // Sonraki adım
  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  // Önceki adım
  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%2343a047" fill-opacity="0.03"%3E%3Ccircle cx="30" cy="30" r="4"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
        opacity: 0.4
      }
    }}>
      <Container maxWidth="xl" sx={{ py: { xs: 2, md: 4 }, px: { xs: 1, md: 3 }, position: 'relative', zIndex: 1 }}>
        {/* Hero Section */}
        <Fade in={true}>
          <Paper 
            elevation={0}
            sx={{ 
              background: 'rgba(255,255,255,0.95)',
              backdropFilter: 'blur(20px)',
              borderRadius: 4,
              p: { xs: 3, md: 5 },
              mb: 4,
              textAlign: 'center',
              border: '1px solid rgba(255,255,255,0.3)',
              boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
            }}
          >
            <Box sx={{ maxWidth: '800px', mx: 'auto' }}>
              <Zoom in={true} style={{ transitionDelay: '200ms' }}>
                <Avatar sx={{ 
                  bgcolor: 'success.main', 
                  width: 80, 
                  height: 80, 
                  mx: 'auto', 
                  mb: 3,
                  boxShadow: '0 8px 24px rgba(76, 175, 80, 0.3)'
                }}>
                  <EcoIcon sx={{ fontSize: 40 }} />
                </Avatar>
              </Zoom>
              
              <Typography 
                variant={isMobile ? "h4" : "h2"} 
                color="primary" 
                gutterBottom 
                sx={{ 
                  fontWeight: 700,
                  background: 'linear-gradient(45deg, #2e7d32 30%, #43a047 90%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 2
                }}
              >
                Karbon Ayak İzi Hesaplama Aracı
              </Typography>
              
              <Typography 
                variant="h6" 
                color="text.secondary" 
                sx={{ mb: 4, lineHeight: 1.6 }}
              >
                Kurumunuzun karbon emisyonlarını hassas bir şekilde hesaplayın ve sürdürülebilirlik hedeflerinizi bilimsel verilerle destekleyin
              </Typography>
              
              <Stack 
                direction={isMobile ? "column" : "row"} 
                spacing={2} 
                justifyContent="center"
                flexWrap="wrap"
                sx={{ gap: 1 }}
              >
                {[
                  { label: "🏢 Sektör Bazlı Analiz", color: "primary" },
                  { label: "📊 Scope 1,2,3 Uyumlu", color: "secondary" },
                  { label: "📋 PDF/CSV Export", color: "success" },
                  { label: "🇹🇷 Türkiye Faktörleri", color: "info" }
                ].map((chip, index) => (
                  <Zoom in={true} style={{ transitionDelay: `${300 + index * 100}ms` }} key={index}>
                    <Chip 
                      label={chip.label} 
                      color={chip.color} 
                      variant="filled"
                      sx={{ 
                        fontWeight: 600,
                        fontSize: '0.9rem',
                        py: 2,
                        px: 1,
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          transition: 'transform 0.2s ease'
                        }
                      }}
                    />
                  </Zoom>
                ))}
              </Stack>
            </Box>
          </Paper>
        </Fade>

        {/* Modern Progress Stepper */}
        <Grow in={true} style={{ transitionDelay: '400ms' }}>
          <Paper 
            elevation={0}
            sx={{ 
              mb: 4, 
              maxWidth: '1200px', 
              mx: 'auto',
              background: 'rgba(255,255,255,0.9)',
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              border: '1px solid rgba(255,255,255,0.3)',
              overflow: 'hidden'
            }}
          >
            <Box sx={{ 
              background: 'linear-gradient(90deg, rgba(76, 175, 80, 0.08) 0%, rgba(67, 160, 71, 0.08) 100%)',
              p: 3 
            }}>
              <Typography variant="h6" fontWeight="600" color="primary" sx={{ mb: 3, textAlign: 'center' }}>
                📋 Hesaplama Süreci
              </Typography>
              
              <Stepper 
                activeStep={activeStep} 
                alternativeLabel
                sx={{
                  '& .MuiStepLabel-root': {
                    cursor: 'pointer'
                  },
                  '& .MuiStepIcon-root': {
                    fontSize: '2rem',
                    '&.Mui-active': {
                      color: 'primary.main',
                      transform: 'scale(1.2)',
                      transition: 'transform 0.3s ease'
                    },
                    '&.Mui-completed': {
                      color: 'success.main'
                    }
                  }
                }}
              >
                {steps.map((step, index) => (
                  <Step key={index}>
                    <StepLabel 
                      icon={
                        <Avatar sx={{ 
                          bgcolor: activeStep === index ? 'success.main' : 
                                  activeStep > index ? 'success.light' : 'grey.300',
                          color: 'white',
                          width: 48,
                          height: 48,
                          transition: 'all 0.3s ease',
                          transform: activeStep === index ? 'scale(1.1)' : 'scale(1)',
                          boxShadow: activeStep === index ? '0 4px 12px rgba(76, 175, 80, 0.4)' : 'none'
                        }}>
                          {step.icon}
                        </Avatar>
                      }
                    >
                      <Box sx={{ mt: 1 }}>
                        <Typography 
                          variant={activeStep === index ? "subtitle1" : "body2"}
                          fontWeight={activeStep === index ? 600 : 400}
                          color={activeStep === index ? "success.main" : "text.secondary"}
                        >
                          {step.label}
                        </Typography>
                        {!isMobile && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            {step.description}
                          </Typography>
                        )}
                      </Box>
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
              
              {isLoading && (
                <Box sx={{ mt: 3 }}>
                  <LinearProgress 
                    sx={{ 
                      height: 8, 
                      borderRadius: 4,
                      backgroundColor: 'rgba(76, 175, 80, 0.2)',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 4,
                        background: 'linear-gradient(90deg, #43a047 0%, #66bb6a 100%)'
                      }
                    }} 
                  />
                  <Typography variant="body2" align="center" sx={{ mt: 2, fontWeight: 500 }}>
                    ⚡ Karbon ayak izi hesaplanıyor...
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grow>

      {/* Modern Kaydedilmiş Hesaplamalar */}
      {savedCalculations.length > 0 && activeStep === 0 && (
        <Grow in={true} style={{ transitionDelay: '600ms' }}>
          <Paper 
            elevation={0}
            sx={{ 
              mb: 4,
              background: 'rgba(255,255,255,0.9)',
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              border: '1px solid rgba(255,255,255,0.3)',
              overflow: 'hidden'
            }}
          >
            <Box sx={{ 
              background: 'linear-gradient(90deg, rgba(33, 150, 243, 0.08) 0%, rgba(25, 118, 210, 0.08) 100%)',
              p: 3 
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Avatar sx={{ bgcolor: 'info.main' }}>
                  <TimelineIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight="600" color="info.main">
                    Geçmiş Hesaplamalar
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Daha önce yapılan hesaplamalara hızlı erişim
                  </Typography>
                </Box>
              </Box>
              
              <Grid container spacing={2}>
                {savedCalculations.slice(0, 6).map((calc, index) => (
                  <Grid item xs={12} sm={6} md={4} key={calc.id}>
                    <Zoom in={true} style={{ transitionDelay: `${700 + index * 100}ms` }}>
                      <Card 
                        sx={{ 
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                          }
                        }}
                        onClick={() => loadSavedCalculation(calc)}
                      >
                        <CardContent sx={{ p: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Avatar sx={{ bgcolor: 'warning.main', width: 32, height: 32 }}>
                              <SpeedIcon fontSize="small" />
                            </Avatar>
                            <Typography variant="subtitle2" fontWeight="600">
                              {calc.sector || 'Sektör'}
                            </Typography>
                          </Box>
                          <Typography variant="h6" color="error.main" fontWeight="bold">
                            {calc.totalEmissions?.toFixed(2)} tCO2e
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(calc.timestamp).toLocaleDateString('tr-TR')}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Zoom>
                  </Grid>
                ))}
              </Grid>
              
              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block', textAlign: 'center' }}>
                💡 Geçmiş hesaplamalarınızdan birini seçerek detayları görüntüleyebilirsiniz
              </Typography>
            </Box>
          </Paper>
        </Grow>
      )}

      {/* Form Adımı */}
      {activeStep === 0 && (
        <Fade in={true}>
          <Box sx={{ maxWidth: '1200px', mx: 'auto' }}>
            <CarbonFootprintForm
              onCalculationComplete={handleCalculationComplete}
              initialData={null}
            />
          </Box>
        </Fade>
      )}

      {/* Sonuçlar Adımı */}
      {activeStep === 1 && calculationData && (
        <Fade in={true}>
          <Box sx={{ maxWidth: '1400px', mx: 'auto' }}>
            <CarbonFootprintReport
              calculationData={calculationData}
              onReset={handleReset}
            />
            
            {/* Navigation Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 4 }}>
              <MDButton
                variant="outlined"
                onClick={handleBack}
              >
                ← Geri Dön
              </MDButton>
              <MDButton
                variant="contained"
                color="primary"
                onClick={handleNext}
              >
                Raporu Tamamla →
              </MDButton>
            </Box>
          </Box>
        </Fade>
      )}

      {/* Final Adımı */}
      {activeStep === 2 && calculationData && (
        <Fade in={true}>
          <Card elevation={3} sx={{ textAlign: 'center', p: 4 }}>
            <Typography variant="h4" color="success.main" gutterBottom>
              ✅ Hesaplama Tamamlandı!
            </Typography>
            <Typography variant="h6" color="textSecondary" sx={{ mb: 3 }}>
              Karbon ayak izi hesaplamanız başarıyla tamamlandı ve kaydedildi.
            </Typography>
            
            <Box sx={{ mb: 3 }}>
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="subtitle1">
                  Toplam Emisyon: <strong>{calculationData.results.total_emissions.ton_co2e.toFixed(2)} tCO2e</strong>
                </Typography>
                {calculationData.results.sector_intensity?.label && (
                  <Typography variant="body2">
                    Sektör Yoğunluğu: {Object.values(calculationData.results.sector_intensity).find(val => typeof val === 'number')?.toFixed(2)} {calculationData.results.sector_intensity.label}
                  </Typography>
                )}
              </Alert>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
              <MDButton
                variant="outlined"
                onClick={() => setActiveStep(1)}
              >
                📊 Raporu Görüntüle
              </MDButton>
              <MDButton
                variant="contained"
                color="primary"
                onClick={handleReset}
              >
                🔄 Yeni Hesaplama
              </MDButton>
            </Box>

            {/* İpuçları */}
            <Box sx={{ mt: 4, textAlign: 'left' }}>
              <Typography variant="h6" gutterBottom color="primary">
                💡 Sonraki Adımlar
              </Typography>
              <Box component="ul" sx={{ pl: 2 }}>
                <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                  Emisyon azaltım hedefleri belirleyin
                </Typography>
                <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                  Enerji verimliliği projelerini değerlendirin
                </Typography>
                <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                  Yenilenebilir enerji kaynaklarını araştırın
                </Typography>
                <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                  Periyodik olarak emisyonlarınızı takip edin
                </Typography>
              </Box>
            </Box>
          </Card>
        </Fade>
      )}

      {/* Bilgi Notları */}
      {activeStep === 0 && (
        <Card elevation={1} sx={{ mt: 4, bgcolor: '#f8f9fa' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom color="primary">
              ℹ️ Kullanım Bilgileri
            </Typography>
            <Box component="ul" sx={{ pl: 2, m: 0 }}>
              <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                <strong>Sektör Seçimi:</strong> İşletmenizin faaliyet gösterdiği ana sektörü seçin
              </Typography>
              <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                <strong>Gerekli Alanlar:</strong> Kırmızı işaretli alanlar hesaplama için zorunludur
              </Typography>
              <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                <strong>Opsiyonel Alanlar:</strong> Daha detaylı analiz için ekstra verileri girebilirsiniz
              </Typography>
              <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                <strong>Birimler:</strong> Her alan için doğru birimi kullandığınızdan emin olun
              </Typography>
              <Typography component="li" variant="body2">
                <strong>Sonuçlar:</strong> Hesaplama sonuçları Scope 1, 2, 3 kategorilerinde gösterilir
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}
      </Container>
    </Box>
  );
};

export default CarbonCalculator;
